/**
 * O aviso de que a API trocou o modelo de uma conversa OPENCODE porque o
 * provedor dele não é mais o ativo (F2 da revisão de out/2026, D46).
 *
 * **O defeito que ele existe para evitar.** Com o NanoGPT ativo, o `serve` só
 * tem a chave do NanoGPT no ambiente: uma conversa gravada com modelo do
 * OpenRouter não responderia — o daemon a recusaria com motivo, turno a turno.
 * O dono decidiu (D46) que ela SEGUE no padrão do provedor ativo; trocar calado
 * faria a conversa mudar de modelo no meio sem ninguém saber por quê. Então a
 * troca é gravada na própria conversa, com o de, o para e o motivo.
 *
 * **Esta carga não é prosa**, como o `erro-do-motor`: com tipo próprio ela fica
 * na conversa e fora do índice (`remoto_texto_do_evento` só lê `user` e
 * `assistant`) — o cérebro não acharia "o modelo mudou" como fala do modelo. O
 * caso `opencodeModeloTrocado` das fixturas de texto prende isso nos três
 * lados (TS, SQL e Python).
 */

/** O `tipo` do `EventoSessao`. Nome novo no eixo `tipo`, que é `z.string()`: sem versão de protocolo. */
export const TIPO_MODELO_TROCADO = 'modelo-trocado';

// `type`, e não `interface`: a carga entra num `Record<string, unknown>` (a do evento), e só o alias tem a assinatura de índice implícita.
export type CargaDoModeloTrocado = {
  type: typeof TIPO_MODELO_TROCADO;
  motor: 'OPENCODE';
  /** O modelo gravado na conversa antes da troca. */
  de: string;
  /** O modelo em que ela segue. */
  para: string;
  /** Por quê, em pt-BR, para a tela mostrar como veio. */
  motivo: string;
};

export function cargaDoModeloTrocado(de: string, para: string, motivo: string): CargaDoModeloTrocado {
  return { type: TIPO_MODELO_TROCADO, motor: 'OPENCODE', de, para, motivo };
}

/** O que a tela desenha de uma troca: o de, o para e, quando veio, o motivo. */
export interface ModeloTrocado {
  de: string;
  para: string;
  /** Ausente quando a carga não trouxe texto: a tela não inventa um porquê. */
  motivo?: string;
}

/**
 * A volta: da carga gravada para o que o painel desenha (F2, D46).
 *
 * Existe pela mesma razão do `erroDoMotorDaCarga`: o front não lê chave crua,
 * e a ida e a volta são provadas juntas num teste de ida-e-volta — duas
 * leituras escritas à mão divergem.
 *
 * `null` quando falta o de ou o para, ou quando os dois são iguais: um aviso
 * de "mudou de X para X" alarmaria sem dizer nada, e a troca que não troca não
 * é troca (a mesma régua da troca de modelo do Claude).
 */
export function modeloTrocadoDaCarga(carga: unknown): ModeloTrocado | null {
  if (typeof carga !== 'object' || carga === null) return null;
  const { de, para, motivo } = carga as { de?: unknown; para?: unknown; motivo?: unknown };
  if (typeof de !== 'string' || typeof para !== 'string') return null;
  const antes = de.trim();
  const depois = para.trim();
  if (antes === '' || depois === '' || antes === depois) return null;
  const porque = typeof motivo === 'string' ? motivo.trim() : '';
  return { de: antes, para: depois, ...(porque ? { motivo: porque } : {}) };
}
